// onlineAudioCapture.ts — acquire mixed (online call + microphone) audio.
//
// The display capture provides the REMOTE side only (call apps never play your
// own voice back to you); the microphone provides the therapist. The two are
// mixed via Web Audio into a single MediaStream that the existing
// MediaRecorder pipeline records unchanged. Video is requested only because
// the share picker requires it — the video track is stopped immediately and
// never recorded.

export class NoDisplayAudioError extends Error {
  constructor() {
    super("Shared source has no audio track");
    this.name = "NoDisplayAudioError";
  }
}

export class MicrophoneDeniedError extends Error {
  constructor(cause: unknown) {
    super("Microphone unavailable for online recording");
    this.name = "MicrophoneDeniedError";
    (this as any).cause = cause;
  }
}

/**
 * Online capture needs tab/system audio from getDisplayMedia, which only
 * Chromium-based desktop browsers support. Firefox/Safari expose
 * getDisplayMedia but cannot capture audio, so gating on the API alone would
 * show a button that always dead-ends.
 */
export function isOnlineCaptureSupported(nav: Navigator = navigator): boolean {
  if (typeof nav.mediaDevices?.getDisplayMedia !== "function") return false;

  const uaData = (nav as any).userAgentData;
  if (uaData) {
    if (uaData.mobile === true) return false;
    return (
      Array.isArray(uaData.brands) &&
      uaData.brands.some((b: { brand: string }) => /Chromium/i.test(b.brand))
    );
  }

  // UA-string fallback for Chromium without userAgentData
  const ua = nav.userAgent || "";
  return /Chrome\//.test(ua) && !/Mobile|Android/i.test(ua);
}

export interface OnlineCallAudio {
  /** Mixed mic + call audio — feed this to MediaRecorder. */
  stream: MediaStream;
  /** Stops display, mic, and mixed tracks; closes the AudioContext. Idempotent. */
  cleanup: () => void;
}

/**
 * Opens the browser share picker and returns the mixed audio stream.
 *
 * @param onEnded fires when the shared source's audio track ends (the browser
 *   "Stop sharing" bar, or the shared tab was closed).
 * @throws NoDisplayAudioError when the picked source has no audio track
 *   (picked a window, or the "share audio" checkbox was left unticked).
 * @throws MicrophoneDeniedError when getUserMedia fails after a successful share.
 * @throws DOMException (NotAllowedError) untouched when the picker is cancelled.
 *   Note: NotAllowedError can also mean a permissions-policy denial in embedded
 *   contexts; Affektra runs top-level, so it is treated purely as user cancel.
 */
export async function acquireOnlineCallAudio(onEnded: () => void): Promise<OnlineCallAudio> {
  const displayStream = await navigator.mediaDevices.getDisplayMedia({
    video: true, // required for the picker; never recorded
    audio: true,
  });

  // Audio-only product: drop video immediately.
  displayStream.getVideoTracks().forEach((t) => t.stop());

  const displayAudioTrack = displayStream.getAudioTracks()[0];
  if (!displayAudioTrack) {
    displayStream.getTracks().forEach((t) => t.stop());
    throw new NoDisplayAudioError();
  }

  let micStream: MediaStream;
  try {
    // Echo cancellation stays at the browser default (on): it also suppresses
    // speaker-played call audio from doubling into the mic track.
    micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (e) {
    displayStream.getTracks().forEach((t) => t.stop());
    throw new MicrophoneDeniedError(e);
  }

  // Created inside the user-gesture click handler; resume() is defensive
  // insurance against autoplay-policy suspension producing silent recordings.
  const ctx = new AudioContext();
  await ctx.resume();
  const destination = ctx.createMediaStreamDestination();
  ctx.createMediaStreamSource(new MediaStream([displayAudioTrack])).connect(destination);
  ctx.createMediaStreamSource(micStream).connect(destination);

  displayAudioTrack.addEventListener("ended", onEnded);

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    displayAudioTrack.removeEventListener("ended", onEnded);
    displayStream.getTracks().forEach((t) => t.stop());
    micStream.getTracks().forEach((t) => t.stop());
    destination.stream.getTracks().forEach((t) => t.stop());
    void ctx.close().catch((e) => console.error("[onlineAudioCapture] AudioContext.close failed", e));
  };

  return { stream: destination.stream, cleanup };
}
